import {
  Box,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
  Link as MUILink,
  Tab,
  Checkbox,
  IconButton,
  Collapse,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  List,
  ListItemButton,
  ListItemText
} from "@mui/material";
import { Dispatch, SetStateAction, useContext, useState } from "react";
import { Contact, ContactSearchResults, FullContact, Phone, SearchData } from "../types";
import { CustomAvatar } from "../../components/custom-avatar";
import { Link } from "react-router-dom";
import { getLogo } from "../../utils/getLogo";
import Highlighter from "react-highlight-words";
import LoadingSkeleton from "./LoadingSkeleton";
import { formatPhone } from "../shared/phones/formatPhone";
import { logEvent } from "../../utils/analyticsLogger";
import produce from "immer";
import { AddEditContactContext } from "../contact/addEditContact/AddEditContactContext";
import StickyTable from "../../shared/StickyTable";
import useResponsive from "../../../minimals/hooks/useResponsive";
import Iconify from "../../../minimals/components/iconify";

// iOS system colours, so the actions read as "call" and "mail" at a glance.
const IOS_GREEN = "#34C759";
const IOS_BLUE = "#007AFF";

// Same order the desktop table lists them in.
const REP_TYPES = [
  { id: 1, label: "Sales Rep" },
  { id: 2, label: "NY Account Rep" },
  { id: 4, label: "National Account Rep" },
  { id: 3, label: "NJ Account Rep" }
];

const DetailRow = ({
  label,
  children,
  fullWidth
}: {
  label: string;
  children: React.ReactNode;
  fullWidth?: boolean;
}) => (
  <Box sx={fullWidth ? { gridColumn: "1 / -1" } : undefined}>
    <Typography variant="caption" sx={{ color: "text.disabled", display: "block", lineHeight: 1.4 }}>
      {label}
    </Typography>
    <Typography variant="body2" component="div">
      {children}
    </Typography>
  </Box>
);

// phoneTypeList is ["Mobile", "Phone", "Fax"]. Dialling phoneNumbers[0] blindly
// meant a contact whose first number is a fax got a call button that dials the fax.
// Numbers live on the role AND on the contact itself — the profile page merges both
// (About.tsx). Reading only the role's array hid contact-level numbers entirely.
const getDialablePhones = (contact: FullContact | null | undefined) => {
  const rolePhones = contact?.contactRoles?.[0]?.phoneNumbers || [];
  const contactPhones = contact?.phoneNumbers || [];
  const dialable = [...rolePhones, ...contactPhones].filter((p) => p.number && p.type !== "Fax");
  // Mobile first; it's the one most likely to reach the person directly.
  return [...dialable].sort((a, b) => Number(b.type === "Mobile") - Number(a.type === "Mobile"));
};

const getAllPhones = (contact: FullContact) =>
  [...(contact.contactRoles?.[0]?.phoneNumbers || []), ...(contact.phoneNumbers || [])].filter((p) => p.number);

const contactActionSx = (color: string) => ({
  width: 36,
  height: 36,
  color: "#fff",
  backgroundColor: color,
  "&:hover": { backgroundColor: color, filter: "brightness(0.92)" }
});

interface IProps {
  contactsSearchResults: ContactSearchResults | undefined;
  searchData: SearchData;
  handlePageNumberChange: (number: number) => void;
  handleChangeRowsPerPage: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  searchWords: string[];
  isLoading: boolean;
  selectedContactIds: number[];
  setSelectedContactIds: Dispatch<SetStateAction<number[]>>;
  /** Mobile only: rows show a checkbox and tap-to-select instead of navigating. */
  isSelectionMode?: boolean;
}

export default function Contacts(props: IProps) {
  const {
    contactsSearchResults,
    searchData,
    handlePageNumberChange,
    handleChangeRowsPerPage,
    searchWords,
    isLoading,
    selectedContactIds,
    setSelectedContactIds,
    isSelectionMode = false
  } = props;

  const highlightStyles = { backgroundColor: "#F8FF00" };

  // A six-column table can't work on a phone, so mobile renders the same rows as
  // stacked cards instead. Desktop keeps the table untouched.
  const isMobile = useResponsive("down", "md");
  // A refresh is a search running over results we already have. Replacing those with
  // a 10-row skeleton on each keystroke changes the page height constantly, which on
  // iOS reads as the page jumping around while you type.
  const hasResults = Boolean(contactsSearchResults?.data?.length);
  const isRefreshing = isLoading && hasResults;
  const showSkeleton = isLoading && !hasResults;
  // Single-open: two long panels at once turn the list into a wall.
  const [expandedContactId, setExpandedContactId] = useState<number | null>(null);
  // Opened when a contact has more than one dialable number, so the button asks
  // instead of picking for you.
  const [callChoiceContact, setCallChoiceContact] = useState<FullContact | null>(null);

  const handleCheck = (contact: FullContact, checked: boolean) => {
    if (checked) {
      setSelectedContactIds([...selectedContactIds, contact?.id]);
      return;
    }
    setSelectedContactIds(selectedContactIds.filter((id) => id !== contact?.id));
  };

  const getRepNames = (contact: FullContact, repTypeId: number) =>
    contact.contactReps
      .filter((r) => r.contactRepTypeId === repTypeId)
      .map((r) =>
        [r.internalUser?.firstName, r.internalUser?.middleName, r.internalUser?.lastName].filter(Boolean).join(" ")
      );

  const getRepLabels = (contact: FullContact) => {
    const repTypes = [
      { id: 1, prefix: "" },
      { id: 2, prefix: "NY" },
      { id: 4, prefix: "National" },
      { id: 3, prefix: "NJ" }
    ];
    return repTypes.flatMap(({ id, prefix }) =>
      contact.contactReps
        .filter((r) => r.contactRepTypeId === id)
        .map((r) =>
          [prefix, r.internalUser?.firstName, r.internalUser?.middleName, r.internalUser?.lastName]
            .filter(Boolean)
            .join(" ")
        )
    );
  };

  const formatPhoneNumber = (phoneNumber: Phone) => {
    const phone = formatPhone(phoneNumber);
    if (!phone) {
      return "";
    }
    if (phone && phoneNumber.countryCode && phoneNumber.countryCode !== "1") {
      return `+${phoneNumber.countryCode} ${phone}`;
    } else return phone;
  };

  return (
    <>
      {isMobile ? (
        <Box sx={{ opacity: isRefreshing ? 0.5 : 1, transition: "opacity 120ms" }}>
          {!showSkeleton &&
            contactsSearchResults?.data.map((c) => {
              const contactRole = c.contactRoles[0];
              const dialablePhones = getDialablePhones(c);
              const phone = dialablePhones[0] || null;
              const repLabels = getRepLabels(c);
              const isSelected = selectedContactIds?.includes(c.id);
              const isExpanded = expandedContactId === c.id;
              const businessEmail = contactRole?.businessEmail || "";
              const personalEmail = c.personalEmail || "";
              // With one address the button just mails it; with two there's no safe
              // default, so it opens the row and the panel's links become the choice.
              const emails = [businessEmail, personalEmail].filter(Boolean);
              const hasEmailChoice = emails.length > 1;
              return (
                <Box key={c.id} sx={{ borderBottom: "1px solid", borderColor: "divider" }}>
                  <Box sx={{ display: "flex", gap: 1.5, px: 2, py: 1.75 }}>
                    {/* Selection mode borrows the iOS Mail pattern: a circle appears on
                        the left and the whole row toggles instead of expanding. */}
                    {isSelectionMode && (
                      <Checkbox
                        checked={isSelected}
                        onChange={(_, value) => {
                          handleCheck(c, value);
                        }}
                        sx={{ alignSelf: "center", p: 0.5 }}
                      />
                    )}

                    <Box
                      onClick={() => {
                        if (isSelectionMode) {
                          handleCheck(c, !isSelected);
                          return;
                        }
                        setExpandedContactId(isExpanded ? null : c.id);
                      }}
                      sx={{ display: "flex", gap: 1.5, flex: 1, minWidth: 0, cursor: "pointer" }}
                    >
                      <CustomAvatar
                        alt={`${c.firstName || ""} ${c.middleName || ""} ${c.lastName || ""}`}
                        name={`${c.firstName || ""} ${c.middleName || ""} ${c.lastName || ""}`}
                        src={getLogo(contactRole?.businessEmail?.split("@")[1])}
                        sx={{ width: 36, height: 36 }}
                      />
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography variant="subtitle2">
                          <Highlighter
                            autoEscape
                            textToHighlight={`${c.firstName || ""} ${c.middleName || ""} ${c.lastName || ""} ${
                              c.suffix || ""
                            }`}
                            highlightStyle={highlightStyles}
                            searchWords={searchWords}
                          />
                        </Typography>

                        {(contactRole?.role?.name || contactRole?.branch?.company?.name) && (
                          <Typography variant="body2" sx={{ color: "text.secondary" }}>
                            <Box sx={{ fontStyle: "italic", display: "inline" }}>
                              <Highlighter
                                autoEscape
                                textToHighlight={contactRole?.role?.name || ""}
                                highlightStyle={highlightStyles}
                                searchWords={searchWords}
                              />
                            </Box>
                            {contactRole?.role?.name && contactRole?.branch?.company?.name && " at "}
                            <Highlighter
                              autoEscape
                              textToHighlight={contactRole?.branch?.company?.name || ""}
                              highlightStyle={highlightStyles}
                              searchWords={searchWords}
                            />
                          </Typography>
                        )}

                        {repLabels.length > 0 && !isExpanded && (
                          <Typography variant="caption" sx={{ color: "text.disabled", display: "block" }}>
                            {repLabels.join(" \u00b7 ")}
                          </Typography>
                        )}
                      </Box>
                    </Box>

                    {/* Each action keeps its slot whether or not the contact has that
                        detail, so the columns line up down the list. Empty rather than
                        disabled: the action doesn't exist, it isn't unavailable. */}
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={1}
                      sx={{ flexShrink: 0, display: isSelectionMode ? "none" : "flex" }}
                    >
                      <Box sx={{ width: 36, flexShrink: 0 }}>
                        {hasEmailChoice ? (
                          <IconButton
                            aria-label={`Choose an email address for ${c.firstName || ""} ${c.lastName || ""}`}
                            title="Business and personal on file — pick one"
                            onClick={() => setExpandedContactId(c.id)}
                            sx={contactActionSx(IOS_BLUE)}
                          >
                            <Iconify icon="eva:email-fill" width={19} />
                          </IconButton>
                        ) : emails.length === 1 ? (
                          <IconButton
                            aria-label={`Email ${c.firstName || ""} ${c.lastName || ""} (${
                              businessEmail ? "business" : "personal"
                            })`}
                            title={`${businessEmail ? "Business" : "Personal"}: ${emails[0]}`}
                            href={`mailto:${emails[0]}`}
                            sx={contactActionSx(IOS_BLUE)}
                          >
                            <Iconify icon="eva:email-fill" width={19} />
                          </IconButton>
                        ) : null}
                      </Box>
                      <Box sx={{ width: 36, flexShrink: 0 }}>
                        {dialablePhones.length > 1 ? (
                          <IconButton
                            aria-label={`Choose a number to call ${c.firstName || ""} ${c.lastName || ""}`}
                            title={`${dialablePhones.length} numbers on file — pick one`}
                            onClick={() => setCallChoiceContact(c)}
                            sx={contactActionSx(IOS_GREEN)}
                          >
                            <Iconify icon="eva:phone-fill" width={19} />
                          </IconButton>
                        ) : phone?.number ? (
                          <IconButton
                            aria-label={`Call ${c.firstName || ""} ${c.lastName || ""}${
                              phone.type ? ` (${phone.type})` : ""
                            }`}
                            title={`${phone.type || "Phone"}: ${formatPhoneNumber(phone) || phone.number}`}
                            href={`tel:+${phone.countryCode}${phone.number}`}
                            sx={contactActionSx(IOS_GREEN)}
                          >
                            <Iconify icon="eva:phone-fill" width={19} />
                          </IconButton>
                        ) : null}
                      </Box>
                      <Iconify
                        icon="eva:chevron-down-fill"
                        width={20}
                        sx={{
                          flexShrink: 0,
                          alignSelf: "center",
                          color: "text.disabled",
                          transition: "transform 150ms",
                          transform: isExpanded ? "rotate(180deg)" : "none"
                        }}
                      />
                    </Stack>
                  </Box>

                  {/* Everything here is already in the search payload, so opening a row
                      costs no request. */}
                  <Collapse in={isExpanded} unmountOnExit>
                    <Box sx={{ px: 2, pb: 2 }}>
                      {/* Two-up for short values; emails span both columns, since a long
                          address would either break mid-word or leave a ragged row. */}
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                          columnGap: 2,
                          rowGap: 1.25
                        }}
                      >
                        {businessEmail && (
                          <DetailRow label="Business email" fullWidth>
                            <MUILink color="inherit" href={`mailto:${businessEmail}`} sx={{ wordBreak: "break-all" }}>
                              <Highlighter
                                autoEscape
                                textToHighlight={businessEmail}
                                highlightStyle={highlightStyles}
                                searchWords={searchWords}
                              />
                            </MUILink>
                          </DetailRow>
                        )}
                        {personalEmail && (
                          <DetailRow label="Personal email" fullWidth>
                            <MUILink color="inherit" href={`mailto:${personalEmail}`} sx={{ wordBreak: "break-all" }}>
                              <Highlighter
                                autoEscape
                                textToHighlight={personalEmail}
                                highlightStyle={highlightStyles}
                                searchWords={searchWords}
                              />
                            </MUILink>
                          </DetailRow>
                        )}
                        {/* Desktop's Phone column only shows the first number. */}
                        {getAllPhones(c).map((p, index) =>
                          p.number ? (
                            <DetailRow key={`${p.key || p.number}-${index}`} label={p.type || "Phone"}>
                              <MUILink color="inherit" href={`tel:+${p.countryCode}${p.number}`}>
                                <Highlighter
                                  autoEscape
                                  textToHighlight={formatPhoneNumber(p) || p.number}
                                  highlightStyle={highlightStyles}
                                  searchWords={searchWords}
                                />
                              </MUILink>
                              {p.extension ? ` x${p.extension}` : ""}
                            </DetailRow>
                          ) : null
                        )}
                        {REP_TYPES.map(({ id, label }) => {
                          const names = getRepNames(c, id);
                          return names.length > 0 ? (
                            <DetailRow key={id} label={label}>
                              {names.join(", ")}
                            </DetailRow>
                          ) : null;
                        })}

                        <Link
                          to={`../phonebook/contact/profile?id=${c.id}`}
                          style={{ textDecoration: "none", gridColumn: "1 / -1" }}
                          onClick={() => {
                            logEvent("Phonebook", "Clicked to view an individual profile");
                          }}
                        >
                          <Button
                            size="small"
                            variant="outlined"
                            fullWidth
                            endIcon={<Iconify icon="eva:arrow-ios-forward-fill" width={16} />}
                            sx={{ mt: 0.5 }}
                          >
                            View full profile
                          </Button>
                        </Link>
                      </Box>
                    </Box>
                  </Collapse>
                </Box>
              );
            })}
        </Box>
      ) : (
        <StickyTable>
          <TableHead>
            <TableRow>
              <TableCell></TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Phone</TableCell>
              <TableCell>Sales Rep</TableCell>
              <TableCell>Account Rep</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!showSkeleton &&
              contactsSearchResults?.data.map((c) => (
                <TableRow key={c.id}>
                  <TableCell>
                    <Checkbox
                      checked={selectedContactIds?.includes(c.id)}
                      onChange={(_, value) => {
                        handleCheck(c, value);
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Link
                      color="initial"
                      to={`../phonebook/contact/profile?id=${c.id}`}
                      style={{
                        textDecoration: "none",
                        color: "#212B36"
                      }}
                      onClick={() => {
                        logEvent("Phonebook", "Clicked to view an individual profile");
                      }}
                    >
                      <Stack direction="row" spacing={1} alignItems="center">
                        <CustomAvatar
                          alt={`${c.firstName || ""} ${c.middleName || ""} ${c.lastName || ""}`}
                          name={`${c.firstName || ""} ${c.middleName || ""} ${c.lastName || ""}`}
                          src={getLogo(c.contactRoles[0]?.businessEmail?.split("@")[1])}
                        />
                        <Box>
                          <Typography variant="subtitle2">
                            <Highlighter
                              autoEscape
                              textToHighlight={`${c.firstName || ""} ${c.middleName || ""} ${c.lastName || ""} ${
                                c.suffix || ""
                              }`}
                              highlightStyle={highlightStyles}
                              searchWords={searchWords}
                            />
                            {/* <Typography variant="caption" display="inline-block">
                            {c.suffix}
                          </Typography> */}
                          </Typography>
                          {!c.contactRoles[0]?.branch?.company?.name && c.contactRoles[0]?.role?.name}
                          {c.contactRoles[0]?.branch?.company && (
                            <Typography variant="body2">
                              <Box
                                sx={{
                                  fontStyle: "italic",
                                  display: "inline-block"
                                }}
                              >
                                <Highlighter
                                  autoEscape
                                  textToHighlight={c.contactRoles[0]?.role?.name || ""}
                                  highlightStyle={highlightStyles}
                                  searchWords={searchWords}
                                />
                              </Box>{" "}
                              {c.contactRoles[0]?.role?.name && "at "}
                              <Link
                                color="initial"
                                to={`../phonebook/contact/profile?id=${c.id}`}
                                style={{
                                  textDecoration: "none",
                                  color: "#212B36"
                                }}
                              >
                                <Highlighter
                                  autoEscape
                                  textToHighlight={c.contactRoles[0]?.branch?.company?.name}
                                  highlightStyle={highlightStyles}
                                  searchWords={searchWords}
                                />
                              </Link>
                            </Typography>
                          )}
                        </Box>
                      </Stack>
                    </Link>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      <MUILink color="initial" href={`mailto:${c.contactRoles[0]?.businessEmail}`}>
                        <Highlighter
                          autoEscape
                          textToHighlight={c.contactRoles[0]?.businessEmail || ""}
                          highlightStyle={highlightStyles}
                          searchWords={searchWords}
                        />
                      </MUILink>
                    </Typography>
                    <Typography variant="body2">
                      <MUILink color="initial" href={`mailto:${c.personalEmail}`}>
                        <Highlighter
                          autoEscape
                          textToHighlight={c.personalEmail || ""}
                          highlightStyle={highlightStyles}
                          searchWords={searchWords}
                        />
                      </MUILink>
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" noWrap>
                      <MUILink
                        color="initial"
                        href={`tel:+${c.contactRoles[0]?.phoneNumbers[0]?.countryCode}${
                          c.contactRoles[0]?.phoneNumbers && c.contactRoles[0]?.phoneNumbers[0]?.number
                        }`}
                      >
                        {c.contactRoles[0]?.phoneNumbers && c.contactRoles[0]?.phoneNumbers[0] && (
                          <Highlighter
                            autoEscape
                            textToHighlight={
                              c.contactRoles[0]?.phoneNumbers &&
                              c.contactRoles[0]?.phoneNumbers[0].number &&
                              formatPhoneNumber(c.contactRoles[0]?.phoneNumbers[0])
                            }
                            highlightStyle={highlightStyles}
                            searchWords={searchWords}
                          />
                        )}
                      </MUILink>
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {c.contactReps
                      .filter((r) => r.contactRepTypeId === 1)
                      .map((r) => (
                        <Typography variant="body2">
                          {r.internalUser?.firstName} {r.internalUser?.middleName} {r.internalUser?.lastName}
                        </Typography>
                      ))}
                  </TableCell>
                  <TableCell>
                    {c.contactReps
                      .filter((r) => r.contactRepTypeId === 2)
                      .map((r) => (
                        <Typography variant="body2" noWrap>
                          <Typography display="inline-block" variant="subtitle2">
                            NY
                          </Typography>{" "}
                          {r.internalUser?.firstName} {r.internalUser?.middleName}
                          {r.internalUser?.lastName}
                        </Typography>
                      ))}

                    {c.contactReps
                      .filter((r) => r.contactRepTypeId === 4)
                      .map((r) => (
                        <Typography variant="body2" noWrap>
                          <Typography display="inline-block" variant="subtitle2">
                            National
                          </Typography>{" "}
                          {r.internalUser?.firstName} {r.internalUser?.middleName}
                          {r.internalUser?.lastName}
                        </Typography>
                      ))}

                    {c.contactReps
                      .filter((r) => r.contactRepTypeId === 3)
                      .map((r) => (
                        <Typography variant="body2" noWrap>
                          <Typography display="inline-block" variant="subtitle2">
                            NJ
                          </Typography>{" "}
                          {r.internalUser?.firstName} {r.internalUser?.middleName}
                          {r.internalUser?.lastName}
                        </Typography>
                      ))}
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </StickyTable>
      )}
      {/* One dialable number dials straight from the row; more than one lands here so
          the choice is explicit rather than decided by array order. */}
      <Dialog
        open={Boolean(callChoiceContact)}
        onClose={() => setCallChoiceContact(null)}
        fullWidth
        maxWidth="xs"
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          {`Call ${callChoiceContact?.firstName || ""} ${callChoiceContact?.lastName || ""}`.trim()}
        </DialogTitle>
        <DialogContent sx={{ px: 0, pb: 1 }}>
          <List disablePadding>
            {getDialablePhones(callChoiceContact).map((p, index) => (
              <ListItemButton
                key={`${p.key || p.number}-${index}`}
                component="a"
                href={`tel:+${p.countryCode}${p.number}`}
                onClick={() => setCallChoiceContact(null)}
                sx={{ px: 3, py: 1.5 }}
              >
                <ListItemText
                  primary={formatPhoneNumber(p) || p.number}
                  secondary={`${p.type || "Phone"}${p.extension ? ` · ext. ${p.extension}` : ""}`}
                  primaryTypographyProps={{ variant: "subtitle2" }}
                />
                <Box
                  sx={{
                    ...contactActionSx(IOS_GREEN),
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: "50%",
                    flexShrink: 0
                  }}
                >
                  <Iconify icon="eva:phone-fill" width={19} />
                </Box>
              </ListItemButton>
            ))}
          </List>
        </DialogContent>
      </Dialog>

      {showSkeleton && <LoadingSkeleton />}
      {contactsSearchResults?.data.length === 0 && (
        <Box textAlign="center" padding={3}>
          <Typography>Your search for "{searchData.searchTerm}" didn't return any results</Typography>
          <Typography variant="caption">Check for spelling mistakes</Typography>
        </Box>
      )}
      <TablePagination
        // The label costs more width than it earns on a phone.
        labelRowsPerPage={isMobile ? "" : "Rows per page:"}
        rowsPerPageOptions={[10, 25, 100]}
        component="div"
        count={contactsSearchResults?.count || 0}
        rowsPerPage={searchData.pageSize}
        page={searchData.pageNumber}
        onPageChange={(e, number) => handlePageNumberChange(number)}
        onRowsPerPageChange={(e) => handleChangeRowsPerPage(e)}
      />
    </>
  );
}
